<template>
  <div
    :class="[$.board_container, 'board_container']"
  >
    <template v-if="showFilterStub">
      <div
        :class="$.no_tasks_stub"
      >
        <svgImage
          :class="$.stub_svg"
          name="comment-hub-no-search"
        />
        <div
          :class="$.no_tasks_text"
        >
          {{ locale('board_no_filtered_tasks') }}
        </div>
      </div>
    </template>
    <template
      v-if="showSkeleton"
    >
      <board-skeleton />
    </template>
    <template
      v-if="showColumns"
    >
      <draggable
        :class="[
          $.columns_container,
          'gantt_scroll',
          'thumb-spaced',
          columnDragging || taskDragging ? $.drag_active : '',
        ]"
        :group="{ name: 'columns' }"
        :value="columns"
        v-bind="{
          disabled: taskCardEditing || taskSettingsInfo,
          group: 'columns',
          animation: 200,
          filter: '.add_field_option',
          ghostClass: $.drop_preview,
          handle: '.column_drag_handle',
          forceFallback: true,
          fallbackClass: $.column_drag,
          fallbackTolerance: 5,
          chosenClass: $.column_drag_chosen,
          easing: 'cubic-bezier(1, 0, 0, 1)',
          scrollSensitivity: 300,
          scrollSpeed: 25
        }"
        :move="onColumnMove"
        @start="onColumnDragStart"
        @end="onColumnDrop"
      >
        <column
          v-for="(column, index) in columns"
          :key="`${column.id}_${column.value}`"
          :style="{ width: `${colWidth}px` }"
          :is-collapsed="!!collapsed[column.id]"
          :tasksMap="tasksMap"
          :projectResources="projectResources"
          :column="column"
          :order="[index, columns.length]"
          :taskDragging="taskDragging"
          :acceptDragItem="!dragPutRestricted.includes(column.id)"
          :canDragTasks="canDragTasks"
          :boardHeight="boardHeight"
          :draftTasks="draftTasks"
          @onTaskDrop="onTaskDrop"
          @onCollapse="onCollapse"
          @taskChange="taskChange"
          @createTask="createTask"
          @taskDragStart="taskDragStart"
        />
        <div
          v-if="groupingFieldInfo.isCustom && canEditCustomFields"
          :class="[$.add_option, 'add_field_option']"
        >
          <iconButton
            :class="$.add_option__button"
            :icon="{ name: 'plus-1', type: 'regular', size: 24 }"
            @onClick="editField"
          />
        </div>
      </draggable>
    </template>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import { mapGetters } from 'vuex';

import Column from './Column.vue';
import iconButton from '$$vueCmp/globalButton/withIcon/iconButton.vue';
import svgImage from '$$vueCmp/VueIcon/vgpSvgImage.vue';

import rights from '../../../components/rights';
import BoardSkeleton from './BoardSkeleton.vue';

export default {
  name: 'BoardView',
  components: {
    BoardSkeleton,
    draggable,
    Column,
    iconButton,
    svgImage,
  },
  inject: ['closeContextMenus'],
  props: {
    columns: {
      type: Array,
      default: []
    },
    colWidth: {
      type: Number,
      default: 324
    },
    showFilterStub: {
      type: Boolean,
      default: false
    },
    columnDragging: {
      type: Boolean,
      default: false
    },
    taskDragging: {
      type: Boolean,
      default: false
    },
    collapsed: {
      type: Object,
      default: {}
    },
    dragPutRestricted: {
      type: Array,
      default: []
    },
    canDragTasks: {
      type: Boolean,
      default: false
    },
    tasksMap: {
      type: Object,
      default: {}
    },
    projectResources: {
      type: Object,
      default: {}
    },
    boardHeight: {
      type: Number,
      default: 1500
    },
    draftTasks: {
      type: Array,
      default: []
    }
  },
  data() {
    return {
      locale: __
    }
  },
  computed: {
    canEditCustomFields() {
      return rights.account.hasRight('custom_fields_edit');
    },
    showSkeleton() {
      return !this.columns.length;
    },
    showColumns() {
      return !(this.showFilterStub || this.showSkeleton);
    },
    ...mapGetters({
      groupingFieldInfo: 'board/groupingFieldInfo',
      taskCardEditing: 'board/taskCardEditing',
      taskSettingsInfo: 'board/taskSettingsInfo',
      projectId: 'board/projectId',
    })
  },
  methods: {
    onColumnDragStart(e) {
      this.closeContextMenus();

      const fallbackNode = Array.from(e.from.children).find(child => child.classList.contains('sortable-drag'));

      if (fallbackNode) {
        fallbackNode.children[1].scrollTop = e.item.__vue__.scrollTop;
      }

      this.$emit('onColumnDragStart');
    },
    onColumnDrop(e) {
      this.$emit('onColumnDrop', {
        addedIndex: e.newIndex,
        removedIndex: e.oldIndex,
        payload: this.columns[e.oldIndex]
      });

      this.$nextTick(() => {
        const columnElems = document.querySelectorAll('.board_column');

        if (columnElems?.length) {
          columnElems.forEach(el => {
            const scrollTop = el.__vue__.scrollTop;

            if (scrollTop) {
              el.__vue__.scrollColumnTo({ y: scrollTop});
            }
          });
        }
      });
    },
    onColumnMove(e) {
      return !e.related.classList.contains('add_field_option');
    },
    onTaskDrop(e) {
      this.$emit('onTaskDrop', e);
    },
    onCollapse(e) {
      this.$emit('onCollapse', e);
    },
    taskChange(e) {
      this.$emit('taskChange', e);
    },
    createTask(name, columnId, columnComponent) {
      this.$emit('createTask', name, columnId, columnComponent);
    },
    taskDragStart(e) {
      this.$emit('taskDragStart', e);
    },
    editField() {
      this.$emit('editField');
    }
  }
}
</script>

<style module="$" src="../less/board.less"></style>
